import React from "react";

/**
 * Tết Blossom Petal SVGs
 * - Hoa Mai (yellow apricot blossom) - 5 petals, golden yellow
 * - Hoa Đào (peach blossom) - 5 petals, pink
 */

const HoaMaiSVG = ({ size = 24, opacity = 0.9, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    style={style}
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* 5 cánh hoa mai vàng */}
    <g transform="translate(50,50)">
      {[0, 72, 144, 216, 288].map((angle, i) => (
        <ellipse
          key={i}
          cx="0"
          cy="-28"
          rx="14"
          ry="24"
          fill={`rgba(255, ${200 + Math.floor(Math.random() * 30)}, 0, ${opacity})`}
          stroke="rgba(218, 165, 32, 0.6)"
          strokeWidth="0.8"
          transform={`rotate(${angle})`}
        />
      ))}
      {/* Nhị hoa */}
      <circle cx="0" cy="0" r="6" fill="rgba(139, 90, 0, 0.8)" />
      <circle cx="0" cy="0" r="3.5" fill="rgba(255, 215, 0, 0.9)" />
      {/* Các chấm nhị nhỏ */}
      {[0, 60, 120, 180, 240, 300].map((angle, i) => (
        <circle
          key={`stamen-${i}`}
          cx={Math.cos((angle * Math.PI) / 180) * 8}
          cy={Math.sin((angle * Math.PI) / 180) * 8}
          r="1.5"
          fill="rgba(184, 134, 11, 0.7)"
        />
      ))}
    </g>
  </svg>
);

const HoaDaoSVG = ({ size = 24, opacity = 0.9, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    style={style}
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* 5 cánh hoa đào hồng */}
    <g transform="translate(50,50)">
      {[0, 72, 144, 216, 288].map((angle, i) => (
        <ellipse
          key={i}
          cx="0"
          cy="-26"
          rx="16"
          ry="22"
          fill={`rgba(255, ${130 + Math.floor(Math.random() * 50)}, ${170 + Math.floor(Math.random() * 30)}, ${opacity})`}
          stroke="rgba(219, 112, 147, 0.5)"
          strokeWidth="0.8"
          transform={`rotate(${angle})`}
        />
      ))}
      {/* Nhị hoa */}
      <circle cx="0" cy="0" r="5" fill="rgba(199, 21, 133, 0.6)" />
      <circle cx="0" cy="0" r="3" fill="rgba(255, 182, 193, 0.9)" />
      {/* Các chấm nhị nhỏ */}
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
        <circle
          key={`stamen-${i}`}
          cx={Math.cos((angle * Math.PI) / 180) * 7}
          cy={Math.sin((angle * Math.PI) / 180) * 7}
          r="1.2"
          fill="rgba(199, 21, 133, 0.5)"
        />
      ))}
    </g>
  </svg>
);

// Single petal (cánh hoa rơi riêng lẻ)
const CanhHoaMaiSVG = ({ size = 18, opacity = 0.85, style }) => (
  <svg
    width={size}
    height={size * 1.5}
    viewBox="0 0 40 60"
    style={style}
    xmlns="http://www.w3.org/2000/svg"
  >
    <ellipse
      cx="20"
      cy="30"
      rx="16"
      ry="26"
      fill={`rgba(255, 215, 0, ${opacity})`}
      stroke="rgba(218, 165, 32, 0.4)"
      strokeWidth="0.5"
    />
    <path
      d="M20 8 Q22 30 20 52"
      stroke="rgba(184, 134, 11, 0.3)"
      strokeWidth="0.8"
      fill="none"
    />
  </svg>
);

const CanhHoaDaoSVG = ({ size = 18, opacity = 0.85, style }) => (
  <svg
    width={size}
    height={size * 1.5}
    viewBox="0 0 40 60"
    style={style}
    xmlns="http://www.w3.org/2000/svg"
  >
    <ellipse
      cx="20"
      cy="30"
      rx="17"
      ry="25"
      fill={`rgba(255, 150, 180, ${opacity})`}
      stroke="rgba(219, 112, 147, 0.3)"
      strokeWidth="0.5"
    />
    <path
      d="M20 8 Q18 30 20 52"
      stroke="rgba(199, 21, 133, 0.2)"
      strokeWidth="0.8"
      fill="none"
    />
  </svg>
);

// Generate blossom data once
const generateBlossoms = (count = 35) => {
  const blossoms = [];
  for (let i = 0; i < count; i++) {
    // Random type: 0=full hoa mai, 1=full hoa đào, 2=cánh mai, 3=cánh đào
    const type = Math.floor(Math.random() * 4);
    const size = type < 2
      ? 14 + Math.random() * 20  // full flowers: 14-34px
      : 10 + Math.random() * 14; // single petals: 10-24px

    blossoms.push({
      id: i,
      type,
      size,
      left: Math.random() * 100,           // % from left
      delay: Math.random() * 12,            // animation delay 0-12s
      duration: 8 + Math.random() * 10,     // fall duration 8-18s
      swayDuration: 3 + Math.random() * 4,  // horizontal sway 3-7s
      swayAmount: 30 + Math.random() * 60,  // sway px amount
      rotation: Math.random() * 360,         // initial rotation
      spinSpeed: 4 + Math.random() * 8,      // spin duration
      opacity: 0.5 + Math.random() * 0.45,  // 0.5-0.95
    });
  }
  return blossoms;
};

const blossomData = generateBlossoms(35);

// CSS keyframes injected once
const blossomStyles = `
@keyframes tetFall {
  0% {
    transform: translateY(-60px) translateX(0px) rotate(0deg);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(calc(100vh + 60px)) translateX(var(--sway)) rotate(var(--spin-end));
    opacity: 0;
  }
}

@keyframes tetSway {
  0%, 100% {
    transform: translateX(0px);
  }
  25% {
    transform: translateX(calc(var(--sway) * 0.5));
  }
  50% {
    transform: translateX(var(--sway));
  }
  75% {
    transform: translateX(calc(var(--sway) * 0.3));
  }
}

@keyframes tetSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes tetFloat {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-8px) scale(1.05);
  }
}
`;

const BlossomItem = React.memo(({ blossom }) => {
  const { type, size, left, delay, duration, swayAmount, rotation, spinSpeed, opacity } = blossom;

  const containerStyle = {
    position: "absolute",
    left: `${left}%`,
    top: "-60px",
    animation: `tetFall ${duration}s linear ${delay}s infinite`,
    "--sway": `${(Math.random() > 0.5 ? 1 : -1) * swayAmount}px`,
    "--spin-end": `${360 + Math.random() * 720}deg`,
    pointerEvents: "none",
    zIndex: 9998,
  };

  const innerStyle = {
    animation: `tetSpin ${spinSpeed}s linear infinite`,
    transform: `rotate(${rotation}deg)`,
  };

  const renderBlossom = () => {
    switch (type) {
      case 0:
        return <HoaMaiSVG size={size} opacity={opacity} />;
      case 1:
        return <HoaDaoSVG size={size} opacity={opacity} />;
      case 2:
        return <CanhHoaMaiSVG size={size} opacity={opacity} />;
      case 3:
        return <CanhHoaDaoSVG size={size} opacity={opacity} />;
      default:
        return <CanhHoaDaoSVG size={size} opacity={opacity} />;
    }
  };

  return (
    <div style={containerStyle}>
      <div style={innerStyle}>
        {renderBlossom()}
      </div>
    </div>
  );
});

BlossomItem.displayName = "BlossomItem";

/**
 * Snowfall component - now renders Tết blossoms (hoa mai & hoa đào)
 * Keeping export names for backward compatibility
 */
const Snowfall = ({ isEnabled }) => {
  if (!isEnabled) return null;

  return (
    <>
      <style>{blossomStyles}</style>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 9998,
        }}
        aria-hidden="true"
      >
        {blossomData.map((blossom) => (
          <BlossomItem key={blossom.id} blossom={blossom} />
        ))}
      </div>
    </>
  );
};

/**
 * Toggle button - Tết themed (red & gold)
 */
const SnowToggleButton = ({ isEnabled, onToggle }) => {
  return (
    <button
      onClick={onToggle}
      style={{
        position: "fixed",
        bottom: "100px",
        right: "20px",
        zIndex: 9999,
        width: "48px",
        height: "48px",
        borderRadius: "50%",
        border: isEnabled ? "2px solid #DAA520" : "2px solid #ccc",
        background: isEnabled
          ? "linear-gradient(135deg, #C41E3A 0%, #E8352E 50%, #FF6B6B 100%)"
          : "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
        color: isEnabled ? "#FFD700" : "#999",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: isEnabled
          ? "0 4px 15px rgba(196, 30, 58, 0.4), 0 0 20px rgba(255, 215, 0, 0.2)"
          : "0 2px 8px rgba(0,0,0,0.15)",
        transition: "all 0.3s ease",
        fontSize: "22px",
        animation: isEnabled ? "tetFloat 2s ease-in-out infinite" : "none",
      }}
      title={isEnabled ? "Tắt hoa rơi" : "Bật hoa rơi"}
      aria-label={isEnabled ? "Tắt hiệu ứng hoa mai hoa đào" : "Bật hiệu ứng hoa mai hoa đào"}
    >
      {/* Cherry blossom icon */}
      <svg width="24" height="24" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(50,50)">
          {[0, 72, 144, 216, 288].map((angle, i) => (
            <ellipse
              key={i}
              cx="0"
              cy="-22"
              rx="12"
              ry="18"
              fill={isEnabled ? "rgba(255, 215, 0, 0.9)" : "rgba(180, 180, 180, 0.6)"}
              stroke={isEnabled ? "rgba(218, 165, 32, 0.7)" : "rgba(150, 150, 150, 0.5)"}
              strokeWidth="1"
              transform={`rotate(${angle})`}
            />
          ))}
          <circle cx="0" cy="0" r="5" fill={isEnabled ? "rgba(139, 90, 0, 0.8)" : "rgba(120, 120, 120, 0.6)"} />
          <circle cx="0" cy="0" r="3" fill={isEnabled ? "rgba(255, 215, 0, 1)" : "rgba(180, 180, 180, 0.8)"} />
        </g>
      </svg>
    </button>
  );
};

/**
 * Custom hook to manage blossom effect state
 * Keeping name for backward compatibility
 */
const useSnowEffect = () => {
  const [snowEnabled, setSnowEnabled] = React.useState(() => {
    try {
      const saved = localStorage.getItem("tetBlossomEnabled");
      // Default: enabled
      return saved !== null ? saved === "true" : true;
    } catch {
      return true;
    }
  });

  const toggleSnow = React.useCallback(() => {
    setSnowEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("tetBlossomEnabled", String(next));
      } catch {
        // ignore localStorage errors
      }
      return next;
    });
  }, []);

  return { snowEnabled, toggleSnow };
};

export { Snowfall, SnowToggleButton, useSnowEffect };
